import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { CheckboxGroup } from "@/components/ui/checkbox-group";
import {
  DragDropContext,
  Droppable,
  DropResult,
  DragUpdate
} from "@hello-pangea/dnd";
import DraggableColumnItem from "@/components/layouts/ColumnsManagement/DraggableColumnItem";
import { ColumnData } from "@/entities/dataTables/types";
import { ViewDataConfiguration } from "@/entities/views/types";
import {
  updateTempViewColumnsState,
  createColumnLabelResolver,
  initializeColumnsWithLabels,
  getViewColumns,
  getIsCheckboxDisabled,
  getLeadingLockedIds
} from "@/entities/dataTables/helpers";
import { useArrMrr } from "@/services/context/ArrMrrContext";
import SearchInput from "@/components/ui/search-input";
import { APIFieldItem } from "@/pages/settings/types";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";
import { ColumnPicker, Close } from "@dreamhub/designsystem/icons";

const TRANSLATION_KEY = "datatoolbar.display";

export interface DisplayPopoverColumnManagementProps {
  isOpen: boolean;
  selectedView?: ViewDataConfiguration;
  defaultColumnsState: ColumnData[];
  /** Stops the first default column being hidden, and being moved while it is at the top of the list. */
  lockFirstColumn?: boolean;
  columnsState?: ColumnData[];
  onColumnsApply?: (updatedColumns: ColumnData[]) => void;
  tableId: string;
  customFields?: APIFieldItem[];
  onClose?: () => void;
}

const DisplayPopoverColumnManagement: React.FC<
  DisplayPopoverColumnManagementProps
> = ({
  isOpen,
  selectedView,
  defaultColumnsState,
  lockFirstColumn = true,
  columnsState,
  onColumnsApply,
  tableId,
  customFields,
  onClose
}) => {
  const { t } = useTranslation();
  const lockedColumnId = lockFirstColumn
    ? defaultColumnsState[0]?.id
    : undefined;
  const lockableColumnIds = useMemo(
    () => new Set(lockedColumnId ? [lockedColumnId] : []),
    [lockedColumnId]
  );
  const { label: arrMrrLabel, fieldName: arrMrrField } = useArrMrr();

  const [allColumns, setAllColumns] =
    useState<ColumnData[]>(defaultColumnsState);
  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    destinationIndex: number | null;
  }>({
    isDragging: false,
    destinationIndex: null
  });
  const [searchValue, setSearchValue] = useState("");
  // Track if the popover was previously open to detect open transitions
  const wasOpenRef = useRef(false);
  const allColumnsRef = useRef(allColumns);
  allColumnsRef.current = allColumns;

  const isSearching = searchValue.trim() !== "";

  // A view saved with the locked column moved keeps it movable until it's back on top.
  const topLockedColumnIds = useMemo(
    () =>
      getLeadingLockedIds(
        allColumns.map((column) => column.id),
        lockableColumnIds
      ),
    [allColumns, lockableColumnIds]
  );

  const displayedColumns = useMemo(() => {
    if (searchValue.trim() === "") {
      return allColumns;
    }
    return allColumns.filter((column) =>
      column.label?.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [allColumns, searchValue]);

  const labelResolver = useCallback(
    createColumnLabelResolver(
      tableId,
      t,
      arrMrrLabel,
      arrMrrField,
      customFields,
      new Map(defaultColumnsState.map((column) => [column.id, column.label]))
    ),
    [tableId, t, arrMrrLabel, arrMrrField, customFields, defaultColumnsState]
  );

  const applyChanges = useCallback(
    (columns: ColumnData[]) => {
      if (selectedView) {
        updateTempViewColumnsState(columns, selectedView);
      }
      // Always notify parent of column changes so the table can update.
      // Pass the full columns (not filtered) so the parent's columnsState retains
      // deselected entries with isVisible:false — otherwise the Services table's
      // useEffect([defaultColumnsState]) treats the absent column as "missing" and
      // immediately re-adds it, making deselection impossible.
      if (onColumnsApply) {
        onColumnsApply(columns);
      }
    },
    [selectedView, onColumnsApply]
  );

  // Always sync from columnsState (source of truth shared with the table)
  useEffect(() => {
    if (columnsState && columnsState.length > 0) {
      const columnsStateIds = new Set(columnsState.map((col) => col.id));
      // Columns not in columnsState are hidden — append them after the tracked ones
      const hiddenDefaults = defaultColumnsState
        .filter((col) => !columnsStateIds.has(col.id))
        .map((col) => ({
          ...col,
          isVisible: false,
          label: labelResolver(col.id, col.label)
        }));

      const labeled = columnsState.map((col) => ({
        ...col,
        label: labelResolver(col.id, col.label)
      }));

      setAllColumns([
        ...labeled.filter((col) => col.isVisible),
        ...labeled.filter((col) => !col.isVisible),
        ...hiddenDefaults
      ]);
      wasOpenRef.current = isOpen;
      return;
    }

    // Fallback when columnsState is not provided: initialize on popover open
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;

    if (justOpened) {
      const mergedColumns = getViewColumns(selectedView, defaultColumnsState);
      const initializedColumns = initializeColumnsWithLabels(
        mergedColumns,
        defaultColumnsState,
        labelResolver,
        false
      );
      setAllColumns(initializedColumns);
    }
  }, [isOpen, columnsState, selectedView, defaultColumnsState, labelResolver]);

  useEffect(() => {
    if (!isOpen) {
      setSearchValue("");
    }
  }, [isOpen]);

  const handleColumnChangeLocal = useCallback(
    (column: ColumnData, checked: boolean) => {
      const currentColumns = allColumnsRef.current;
      const updatedColumns = currentColumns.map((c) =>
        c.id === column.id ? { ...c, isVisible: checked } : c
      );
      setAllColumns(updatedColumns);
      applyChanges(updatedColumns);
    },
    [applyChanges]
  );

  const handleDragUpdate = (update: DragUpdate) => {
    if (!update.destination) {
      setDragState({ isDragging: true, destinationIndex: null });
      return;
    }
    setDragState({
      isDragging: true,
      destinationIndex: update.destination.index
    });
  };

  const handleDragEnd = (result: DropResult) => {
    setDragState({ isDragging: false, destinationIndex: null });
    const { destination, source } = result;

    if (!destination) return;
    if (destination.index === source.index) return;
    if (isSearching) return;

    const currentColumns = allColumnsRef.current;
    // The locked columns are the top of the list: none may leave it, and nothing may be dropped into it.
    const lockedCount = getLeadingLockedIds(
      currentColumns.map((column) => column.id),
      lockableColumnIds
    ).size;
    if (Math.min(source.index, destination.index) < lockedCount) return;

    const newColumns = [...currentColumns];
    const [removed] = newColumns.splice(source.index, 1);
    newColumns.splice(destination.index, 0, removed);
    const reorderedColumns = newColumns.map((column, index) => ({
      ...column,
      order: index
    }));

    setAllColumns(reorderedColumns);
    applyChanges(reorderedColumns);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between h-8">
          <div className="flex items-center gap-2 pl-1">
            <IconContainer color="secondary" size="5">
              <ColumnPicker />
            </IconContainer>
            <Text variant="bodySecondary" className="text-nowrap">
              {t(`${TRANSLATION_KEY}.tableColumns`)}
            </Text>
          </div>
          <IconButton size="md" variant="iconNoBg" iconColor="text-body-secondary" onClick={onClose}>
            <Close />
          </IconButton>
        </div>
        <SearchInput
          placeholder={t("search")}
          className="p-2"
          value={searchValue}
          onChange={handleSearchChange}
        />
      </div>
      <div className="overflow-y-auto" style={{ maxHeight: "calc(var(--radix-popover-content-available-height) - 260px - 24px)" }}>
        <DragDropContext
          onDragEnd={handleDragEnd}
          onDragUpdate={handleDragUpdate}
        >
          <Droppable
            droppableId="columns-list"
            isDropDisabled={isSearching || displayedColumns.length <= 1}
          >
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                <CheckboxGroup fullWidth spacing={0} color="var(--cta)">
                  {displayedColumns.map((column, index) => (
                    <React.Fragment key={column.id}>
                      <DraggableColumnItem
                        column={column}
                        index={index}
                        onColumnChange={handleColumnChangeLocal}
                        isDragDisabled={
                          isSearching ||
                          displayedColumns.length <= 1 ||
                          topLockedColumnIds.has(column.id)
                        }
                        isCheckboxDisabled={
                          // Only a shown column is locked: one a view already hid can always be shown again.
                          column.isVisible &&
                          (column.id === lockedColumnId ||
                            getIsCheckboxDisabled(column, allColumns.length))
                        }
                      />
                      {dragState.isDragging &&
                        dragState.destinationIndex === index && (
                          <div className="h-[1px] bg-cta w-full transition-opacity duration-200" />
                        )}
                    </React.Fragment>
                  ))}
                </CheckboxGroup>
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    </div>
  );
};

export default DisplayPopoverColumnManagement;
